import { z } from 'zod';

import { TimestampSchema } from './primitives.js';

/**
 * Per-tenant lifetime counters at `tenants/{tid}/stats/lifetime`.
 *
 * Maintained incrementally by triggers — `milestoneFanout` owns `washCount`,
 * `onCustomerIndexed` owns `customerCount` — because the alternative is a
 * collection scan every time something needs to know how far along a tenant is.
 *
 * Both fields are optional: the document is created by whichever trigger fires
 * first, so a tenant that has washed cars but never captured a customer has one
 * field and not the other. Readers default to zero.
 */
export const LifetimeStatsSchema = z.object({
  id: z.string().describe('Doc id — always `lifetime`.'),
  washCount: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Washes ever recorded; drives milestone thresholds and disclosure.'),
  customerCount: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Distinct customers ever indexed for this tenant.'),
  repeatCustomerCount: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe(
      'Customers who came back at least once — incremented on the visits 1→2 ' +
        'transition by `onFirstRepeatCustomer`, once per customer, ever. Paired ' +
        'with `customerCount` it gives the share of customers who returned, ' +
        'which is the one retention number an operator can act on.',
    ),
  updatedAt: TimestampSchema.optional().describe('Last time any counter moved.'),
});
export type LifetimeStats = z.infer<typeof LifetimeStatsSchema>;
